import type { ContentTypeDefinition } from '../../../lib/flotiq-client/types/api-response';

import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getEnv } from '../../../lib/env';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Panel from '../../../components/Panel/Panel';

import { useModals } from '../../../contexts/ModalContext';

import FormGeneratorModal from './FormGeneratorModal';

const More = () => (
  <p>
    <Trans i18nKey="ContentTypeForm.FormGenerator.More">
      Currently, the form supports a limited list of fields. Learn more about
      generating and using the stand-alone forms
      <a
        className="text-blue"
        href={getEnv('VITE_DOCUMENTATION_FORM_GENERATOR')}
        target="_blank"
        rel="noreferrer"
      >
        in the documentation
      </a>
    </Trans>
  </p>
);

interface FormGeneratorPanelProps {
  /**
   * Content type for which form is displayed
   */
  contentType: ContentTypeDefinition;
  /**
   * Component test id
   */
  testId?: string;
}

const FormGeneratorPanel = ({
  contentType,
  testId,
}: FormGeneratorPanelProps) => {
  const { t } = useTranslation();
  const modal = useModals();

  const onGenerate = useCallback(async () => {
    await modal({
      title: t('ContentTypeForm.FormGenerator.Title'),
      content: (
        <div className="space-y-3">
          <More />
          <FormGeneratorModal
            contentType={contentType}
            {...getTestProps(testId, 'form-generator', 'testId')}
          />
        </div>
      ),
      size: 'xl',
    });
  }, [contentType, modal, t, testId]);

  return (
    <Panel
      title={t('ContentTypeForm.FormGenerator.Title')}
      isCollapsable={false}
      additionalContainerClasses="py-5 px-4"
      additionalTitleClasses="text-lg"
      additionalChildrenClasses="pt-2"
      open
    >
      <Trans i18nKey="ContentTypeForm.FormGenerator.Description" />
      <Button
        onClick={onGenerate}
        additionalClasses="my-4"
        {...getTestProps(testId, 'open-form-generator', 'testId')}
      >
        {t('ContentTypeForm.FormGenerator.Generate')}
      </Button>
      <More />
    </Panel>
  );
};

export default FormGeneratorPanel;
